import React, { FC, useEffect, useState } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Slider from "@mui/material/Slider"
import Typography from "@mui/material/Typography"
import SectionWrapper from "./LockedSectionWrapper"
import VideoPlayer from "../../player/VideoPlayer"
import { Participant, Video } from "../../types"

// Where the 360 camera points when the video opens: two sliders over one
// preview of exactly what a viewer sees first (Kai, 4 Nov). This replaces the
// Starting tilt card and the rotation text box that sat in Content.
//
//  - Tilt points the camera up or down (start_tilt). Gavin, 3 Nov: "Head is
//    low — should be centre". It tilts the camera rather than the picture,
//    because a tilted picture would tip the horizon as soon as a viewer panned.
//    Presentation, outside the manifest, so it stays editable once sealed.
//  - Rotation turns the picture left or right under the camera
//    (rotation_adjustment), so the first participant is straight ahead. It is
//    part of the sealed manifest, so it freezes with the video.
//
// The preview opens halfway through (past the intro card, on someone's face),
// faces north, and takes no drag: the sliders are the only thing that moves
// it. Viewers can still look anywhere from where it opens. Letting go of a
// slider saves.

// The player's normal view stops at 45° up or down (VideoPlayer360Controller
// polar limits), and so does the API (data.StartTiltLimit).
const TILT_LIMIT = 45
const ROTATION_LIMIT = 180

const TILT_MARKS = [-45, -30, -15, 0, 15, 30, 45].map((value) => ({ value, label: `${value}°` }))
const ROTATION_MARKS = [-180, -90, 0, 90, 180].map((value) => ({ value, label: `${value}°` }))

interface StartPositionSectionProps {
  video: Video
  participants?: Participant[]
  rotationAdjustment: number
  sealed: boolean
  onSaveTilt: (degrees: number) => void
  onSaveRotation: (degrees: number) => void
}

const StartPositionSection: FC<StartPositionSectionProps> = ({
  video,
  participants,
  rotationAdjustment,
  sealed,
  onSaveTilt,
  onSaveRotation,
}) => {
  const storedTilt = video.start_tilt ?? 0
  const [tilt, setTilt] = useState(storedTilt)
  const [rotation, setRotation] = useState(rotationAdjustment)

  // Follow the server when a stored value changes under us.
  useEffect(() => { setTilt(storedTilt) }, [storedTilt])
  useEffect(() => { setRotation(rotationAdjustment) }, [rotationAdjustment])

  const atDefault = storedTilt === 0 && (sealed || rotationAdjustment === 0)

  return (
    <SectionWrapper
      title="Starting position"
      status="pending"
      actions={
        <Button
          size="small"
          variant="outlined"
          disabled={atDefault}
          onClick={() => {
            if (storedTilt !== 0) {
              setTilt(0)
              onSaveTilt(0)
            }
            if (!sealed && rotationAdjustment !== 0) {
              setRotation(0)
              onSaveRotation(0)
            }
          }}
        >
          Reset
        </Button>
      }
    >
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        What every viewer sees when the video opens. Rotation turns the picture so the first
        participant is straight ahead; tilt points the camera up or down to centre faces.
        The preview starts halfway through and only the sliders move it.
      </Typography>

      {video.stream_url && (
        <Box sx={{ mb: 2 }}>
          <VideoPlayer
            src={video.stream_url}
            type="application/x-mpegURL"
            participants={participants || []}
            rotationAdjustment={rotation}
            startTilt={tilt}
            initialCamera={{ yaw: 0, pitch: tilt }}
            initialTimeFraction={0.5}
            preview
            compact
          />
        </Box>
      )}

      <Box sx={{ px: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Rotation (turn left ← → turn right)
          {sealed && " — sealed with the video, cut a new version to change it"}
        </Typography>
        <Slider
          value={rotation}
          min={-ROTATION_LIMIT}
          max={ROTATION_LIMIT}
          step={1}
          marks={ROTATION_MARKS}
          disabled={sealed}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}°`}
          onChange={(_, v) => setRotation(v as number)}
          onChangeCommitted={(_, v) => onSaveRotation(v as number)}
          aria-label="Starting rotation"
        />

        <Typography variant="caption" sx={{ color: "text.secondary" }}>Tilt (down ← → up)</Typography>
        <Slider
          value={tilt}
          min={-TILT_LIMIT}
          max={TILT_LIMIT}
          step={1}
          marks={TILT_MARKS}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}°`}
          onChange={(_, v) => setTilt(v as number)}
          onChangeCommitted={(_, v) => onSaveTilt(v as number)}
          aria-label="Starting tilt"
        />
      </Box>
    </SectionWrapper>
  )
}

export default StartPositionSection
